/**
 * @description Modal moderno e estilizado reutilizável para qualquer conteúdo
 * @module ModernModal
 * @since Outubro 2025
 */

import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Add as AddIcon,
} from "@mui/icons-material";

interface ModernModalProps {
  // Controle do modal
  open: boolean;
  onClose: () => void;
  
  // Header customizável
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  gradientColors?: [string, string]; // [cor1, cor2] para o gradiente
  
  // Conteúdo do modal (renderizado como children)
  children: React.ReactNode;
  
  // Botões de ação customizáveis
  showActions?: boolean; // Se false, não mostra os botões padrão
  cancelButtonText?: string;
  confirmButtonText?: string;
  onConfirm?: () => void | Promise<void>;
  confirmDisabled?: boolean;
  
  // Estado de loading
  loading?: boolean;
  loadingText?: string;
  
  // Tamanho do modal
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
}

/**
 * Modal moderno com header estilizado
 * Aceita qualquer conteúdo através de children
 * Totalmente customizável para diferentes contextos
 */
const ModernModal: React.FC<ModernModalProps> = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  gradientColors = ["#00A884", "#00C896"], // Verde padrão
  children,
  showActions = true,
  cancelButtonText = "Cancelar",
  confirmButtonText = "Confirmar",
  onConfirm,
  confirmDisabled = false,
  loading = false,
  loadingText = "Processando...",
  maxWidth = "md",
}) => {
  return (
    <Dialog
      open={open}
      onClose={() => !loading && onClose()}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {/* Header com gradiente customizável */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${gradientColors[0]} 0%, ${gradientColors[1]} 100%)`,
          pt: 3,
          pb: 2,
          px: 3,
          position: "relative",
        }}
      >
        <IconButton
          onClick={onClose}
          disabled={loading}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
            "&:hover": { bgcolor: "rgba(255, 255, 255, 0.1)" },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ textAlign: "center", color: "white" }}>
          {/* Ícone grande em círculo com glassmorphism */}
          {icon && (
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: "rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto",
                mb: 2,
                backdropFilter: "blur(10px)",
                border: "3px solid rgba(255, 255, 255, 0.3)",
              }}
            >
              {icon}
            </Box>
          )}
          
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          
          {subtitle && (
            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Conteúdo customizável via children */}
      <DialogContent sx={{ pt: 3, pb: 2, px: 3 }}>
        {children}
      </DialogContent>

      {/* Botões de ação (opcional) */}
      {showActions && (
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 0,
            gap: 1,
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Button
            fullWidth
            onClick={onClose}
            disabled={loading}
            variant="outlined"
            sx={{
              borderColor: "grey.300",
              color: "grey.700",
              textTransform: "none",
              fontWeight: 600,
              py: 1.2,
              "&:hover": {
                borderColor: "grey.400",
                bgcolor: "grey.50",
              },
            }}
          >
            {cancelButtonText}
          </Button>
          
          {onConfirm && (
            <Button
              fullWidth
              onClick={onConfirm}
              variant="contained"
              disabled={confirmDisabled || loading}
              sx={{
                bgcolor: gradientColors[0],
                textTransform: "none",
                fontWeight: 600,
                py: 1.2,
                boxShadow: `0 4px 12px ${gradientColors[0]}4D`,
                "&:hover": {
                  bgcolor: gradientColors[1],
                  boxShadow: `0 6px 16px ${gradientColors[0]}66`,
                },
                "&:disabled": {
                  bgcolor: "grey.300",
                },
              }}
            >
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={20} sx={{ color: "white" }} />
                  {loadingText}
                </Box>
              ) : (
                confirmButtonText
              )}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ModernModal;

